import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sidebar, Menu, MenuItem} from 'react-pro-sidebar';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  FaSearch,
  FaCalculator,
  FaWifi,
  FaRegUser,
  FaFileAlt,
  FaBook,
  FaClone,
  FaSignOutAlt,
  FaSyncAlt,
  FaUserCircle,
} from 'react-icons/fa';
import useUserPermissions from './hooks/useUserPermissions';
import './AppMenu.css';

const palette = {
  bg: '#0f172a',
  bgHover: '#1e293b',
  bgActive: '#2563eb',
  text: '#cbd5e1',
  textMuted: '#94a3b8',
  textActive: '#ffffff',
  border: 'rgba(148, 163, 184, 0.12)',
  brand: '#3b82f6',
};

const menuItemStyles = {
  button: ({ active }) => ({
    backgroundColor: active ? palette.bgActive : 'transparent',
    color: active ? palette.textActive : palette.text,
    borderRadius: 8,
    margin: '2px 12px',
    height: 'auto',
    minHeight: 42,
    paddingTop: 6,
    paddingBottom: 6,
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': {
      backgroundColor: active ? palette.bgActive : palette.bgHover,
      color: palette.textActive,
    },
  }),
  icon: ({ active }) => ({
    color: active ? palette.textActive : palette.textMuted,
    marginRight: 10,
  }),
  label: {
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.25,
  },
  subMenuContent: {
    backgroundColor: 'transparent',
  },
};

const AppMenu = ({ toggled, handleToggleSidebar, empresas }) => {
  const { signOut, user } = useAuthenticator((context) => [context.user]);
  const permissions = useUserPermissions();

  const hasPermission = (permissionName) =>
    permissions.some((p) => p.permission_name === permissionName);

  const empresaNombre = empresas && empresas.length ? empresas[0].name : 'DTE';

  const { pathname } = useLocation();
  const isActive = (path) => pathname === path;

  return (
    <Sidebar
      toggled={toggled}
      onBackdropClick={() => handleToggleSidebar(false)}
      breakPoint="md"
      width="260px"
      rootStyles={{
        border: 'none',
        background: palette.bg,
        color: palette.text,
      }}
    >
      <div className="sidebar-brand">
        <div className="sidebar-brand-badge">DTE</div>
        <div className="sidebar-brand-text">
          <span className="sidebar-brand-title">C2C DTE</span>
          <span className="sidebar-brand-subtitle" title={empresaNombre}>
            {empresaNombre}
          </span>
        </div>
      </div>

      <div className="sidebar-section-label">General</div>
      <Menu menuItemStyles={menuItemStyles}>
        <MenuItem
          icon={<FaSearch />}
          active={isActive('/search') || isActive('/')}
          component={<NavLink to="/search" />}
        >
          Búsqueda
        </MenuItem>
        <MenuItem
          icon={<FaWifi />}
          active={isActive('/estado_cajeros')}
          component={<NavLink to="/estado_cajeros" />}
        >
          Estado Cajeros
        </MenuItem>
      </Menu>

      <div className="sidebar-section-label">Reportes</div>
      <Menu menuItemStyles={menuItemStyles}>
        <MenuItem
          icon={<FaCalculator />}
          active={isActive('/resumendiarioboletas')}
          component={<NavLink to="/resumendiarioboletas" />}
        >
          Resumen Diario de Documentos
        </MenuItem>
        <MenuItem
          icon={<FaCalculator />}
          active={isActive('/resumenmensualboletas')}
          component={<NavLink to="/resumenmensualboletas" />}
        >
          Resumen Mensual
        </MenuItem>
        <MenuItem
          icon={<FaCalculator />}
          active={isActive('/conciliacion')}
          component={<NavLink to="/conciliacion" />}
        >
          Conciliación
        </MenuItem>
        {hasPermission('Cierre Caja') && (
          <MenuItem
            icon={<FaCalculator />}
            active={isActive('/cierrecaja')}
            component={<NavLink to="/cierrecaja" />}
          >
            Cierre de Caja
          </MenuItem>
        )}
        {hasPermission('Sincronizacion SAP') && (
          <MenuItem
            icon={<FaSyncAlt />}
            active={isActive('/resumenSincScharfstein')}
            component={<NavLink to="/resumenSincScharfstein" />}
          >
            Sincronización Documentos
          </MenuItem>
        )}
      </Menu>

      {(hasPermission('Usuarios POS') ||
        hasPermission('Facturacion') ||
        hasPermission('Skualo') ||
        hasPermission('Facturas POS') ||
        hasPermission('Emision Masiva Boletas')) && (
        <>
          <div className="sidebar-section-label">Administración</div>
          <Menu menuItemStyles={menuItemStyles}>
            {hasPermission('Usuarios POS') && (
              <MenuItem
                icon={<FaRegUser />}
                active={isActive('/usuariosposconfig')}
                component={<NavLink to="/usuariosposconfig" />}
              >
                Usuarios POS
              </MenuItem>
            )}
            {hasPermission('Facturacion') && (
              <MenuItem
                icon={<FaBook />}
                active={isActive('/factura')}
                component={<NavLink to="/factura" />}
              >
                Facturación
              </MenuItem>
            )}
            {hasPermission('Skualo') && (
              <MenuItem
                icon={<FaFileAlt />}
                active={isActive('/skualo')}
                component={<NavLink to="/skualo" />}
              >
                Skualo
              </MenuItem>
            )}
            {hasPermission('Facturas POS') && (
              <MenuItem
                icon={<FaFileAlt />}
                active={isActive('/receptorFactura')}
                component={<NavLink to="/receptorFactura" />}
              >
                Receptores Factura POS
              </MenuItem>
            )}
            {hasPermission('Emision Masiva Boletas') && (
              <MenuItem
                icon={<FaClone />}
                active={isActive('/emisionmasiva')}
                component={<NavLink to="/emisionmasiva" />}
              >
                Emisor Masivo Boletas
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <FaUserCircle className="sidebar-user-avatar" />
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">
              {user?.username || 'Usuario'}
            </span>
            <span className="sidebar-user-role">Sesión activa</span>
          </div>
        </div>
        <button className="sidebar-signout" onClick={signOut} type="button">
          <FaSignOutAlt />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </Sidebar>
  );
};

export default AppMenu;
